import { CargoType, CargoRiskData } from '../types';

// English translations for cargo risk details
export const CARGO_RISK_DETAILS_EN: Record<CargoType, CargoRiskData> = {
  [CargoType.GENERAL_MERCHANDISE]: {
    riskFactor: 1.00,
    description: "Standard risk general merchandise that doesn't require special transport conditions.",
    securityRecommendations: [
      "Proper packaging to protect against minor impacts",
      "Secure load with standard straps",
      "Use waterproof tarps during rainy season"
    ]
  },
  [CargoType.PERISHABLE_FOOD]: {
    riskFactor: 1.30,
    description: "Food requiring refrigeration with short expiration dates. High risk due to temperature control needs and delivery time constraints.",
    securityRecommendations: [
      "Transport in units with calibrated refrigeration equipment",
      "Constant temperature monitoring throughout the journey",
      "Route prioritization to minimize transit time",
      "Contingency plan for refrigeration system failures"
    ]
  },
  [CargoType.NON_PERISHABLE_FOOD]: {
    riskFactor: 1.10,
    description: "Food with extended shelf life but requiring protection against moisture and pests.",
    securityRecommendations: [
      "Protection against humidity and temperature changes",
      "Packaging resistant to rodents and insects",
      "Periodic verification of seals and packaging"
    ]
  },
  [CargoType.CONSTRUCTION_MATERIALS]: {
    riskFactor: 1.05,
    description: "Durable materials with low risk of damage during transport.",
    securityRecommendations: [
      "Proper weight distribution in the vehicle",
      "Rain protection for moisture-sensitive materials",
      "Secure fastening to prevent movement"
    ]
  },
  [CargoType.AGRICULTURAL_PRODUCTS]: {
    riskFactor: 1.15,
    description: "Agricultural products susceptible to damage from handling and weather conditions.",
    securityRecommendations: [
      "Adequate ventilation for fresh products",
      "Avoid direct sun exposure",
      "Separation between products to prevent cross-contamination"
    ]
  },
  [CargoType.FURNITURE]: {
    riskFactor: 1.25,
    description: "Furniture vulnerable to scratches, dents, and breakage during transport.",
    securityRecommendations: [
      "Protective packaging on corners and delicate surfaces",
      "Use of separators between pieces",
      "Secure firmly to avoid movement during transport",
      "Careful handling during loading and unloading"
    ]
  },
  [CargoType.CLOTHING_TEXTILES]: {
    riskFactor: 1.08,
    description: "Textile products with moderate risk of damage from moisture or contaminants.",
    securityRecommendations: [
      "Waterproof protection against liquids",
      "Packaging that prevents stains and odors",
      "Ensure the loading area is clean"
    ]
  },
  [CargoType.ELECTRONICS]: {
    riskFactor: 1.40,
    description: "High-value and fragile electronic equipment, sensitive to impacts and environmental conditions.",
    securityRecommendations: [
      "Packaging with anti-static protection",
      "Shock-absorbing material to absorb impacts",
      "Temperature and humidity control",
      "Enhanced security against theft",
      "Avoid exposure to strong electromagnetic fields"
    ]
  },
  [CargoType.AUTOMOTIVE_PARTS]: {
    riskFactor: 1.15,
    description: "Automotive components that may include heavy and precision parts.",
    securityRecommendations: [
      "Resistant packaging for heavy parts",
      "Anti-corrosion protection for exposed metals",
      "Clear identification of fragile or precision parts"
    ]
  },
  [CargoType.HOUSEHOLD_APPLIANCES]: {
    riskFactor: 1.25,
    description: "Appliances with fragile components and surfaces susceptible to damage.",
    securityRecommendations: [
      "Original manufacturer packaging when possible",
      "Additional reinforcement on corners and edges",
      "Fastening that prevents any movement within the vehicle",
      "Handling by trained personnel"
    ]
  },
  [CargoType.STEEL_METALS]: {
    riskFactor: 1.05,
    description: "Resistant but heavy metal products requiring weight distribution considerations.",
    securityRecommendations: [
      "Proper weight distribution on the platform",
      "Moisture protection to prevent oxidation",
      "Reinforced fastening to prevent shifting"
    ]
  },
  [CargoType.CHEMICALS]: {
    riskFactor: 1.50,
    description: "Chemical products that may be corrosive, flammable, or reactive, requiring specialized handling.",
    securityRecommendations: [
      "Strict compliance with regulations for chemical transport",
      "Proper separation between incompatible products",
      "Specialized equipment and signage according to standards",
      "Personnel certified for chemical handling",
      "Emergency response plan for spills"
    ]
  },
  [CargoType.HAZARDOUS_MATERIALS]: {
    riskFactor: 1.80,
    description: "Materials with hazard classification requiring special permits and strict protocols.",
    securityRecommendations: [
      "Mandatory special permits and documentation",
      "Units certified for hazardous materials transport",
      "Drivers with special training and certification",
      "Predetermined routes with risk assessment",
      "Coordination with emergency authorities",
      "Secondary containment systems for spills"
    ]
  },
  [CargoType.FRAGILE_ITEMS]: {
    riskFactor: 1.45,
    description: "Highly fragile items requiring specialized packaging and careful handling.",
    securityRecommendations: [
      "High-protection packaging with shock-absorbing materials",
      "Clear fragility labeling on all packages",
      "Separate loading from heavy items",
      "Periodic checks during the journey",
      "Smooth driving avoiding sudden maneuvers"
    ]
  },
  [CargoType.HIGH_VALUE_GOODS]: {
    riskFactor: 1.60,
    description: "High-value merchandise that represents an elevated theft risk and requires intensive security measures.",
    securityRecommendations: [
      "Security escorts throughout the journey",
      "Vehicles with GPS systems and real-time tracking",
      "Variable and unpredictable routes",
      "Constant communication with operations base",
      "Specialized high-value insurance",
      "Verified drivers with clean background checks"
    ]
  },
  [CargoType.MACHINERY]: {
    riskFactor: 1.30,
    description: "Heavy equipment and machinery with sensitive components and complex weight distribution.",
    securityRecommendations: [
      "Prior study of routes to verify bridge and road capacity",
      "Special permits for oversized loads",
      "Reinforced anchors and fastenings",
      "Special protection for hydraulic and electronic components",
      "Escort vehicles for special dimensions"
    ]
  },
  [CargoType.LIVESTOCK]: {
    riskFactor: 1.45,
    description: "Live animals requiring animal welfare, feeding, and rest considerations.",
    securityRecommendations: [
      "Vehicles conditioned for animal transport",
      "Controlled ventilation and temperature",
      "Scheduled stops for feeding and rest",
      "Personnel trained in livestock handling",
      "Health and movement permits",
      "Contingency plan for veterinary emergencies"
    ]
  },
  [CargoType.PHARMACEUTICALS]: {
    riskFactor: 1.40,
    description: "Pharmaceutical products sensitive to temperature that may require cold chain and additional security.",
    securityRecommendations: [
      "Continuous temperature control and recording",
      "Validation of refrigeration equipment",
      "Qualified thermal packaging",
      "Enhanced security against theft",
      "Exhaustive documentation of the chain of custody",
      "Procedures for managing temperature deviations"
    ]
  },
  [CargoType.WOOD_PRODUCTS]: {
    riskFactor: 1.05,
    description: "Relatively resistant wood products that require protection against moisture.",
    securityRecommendations: [
      "Protection against rain and excessive humidity",
      "Proper fastening to prevent shifting",
      "Edge protection for fine or treated woods"
    ]
  },
  [CargoType.OTHER]: {
    riskFactor: 1.20,
    description: "General category for merchandise that doesn't fit in other classifications, a moderate risk factor is applied.",
    securityRecommendations: [
      "Case-by-case assessment of specific risk",
      "Appropriate packaging according to product characteristics",
      "Detailed documentation of contents",
      "Special considerations to be determined according to the merchandise"
    ]
  }
};

// Spanish original cargo risk details (from types.ts)
export const CARGO_RISK_DETAILS_ES: Record<CargoType, CargoRiskData> = {
  [CargoType.GENERAL_MERCHANDISE]: {
    riskFactor: 1.00,
    description: "Mercancía general de riesgo estándar que no requiere condiciones especiales de transporte.",
    securityRecommendations: [
      "Embalaje adecuado para proteger contra golpes leves",
      "Asegurar carga con correas estándar",
      "Utilizar lonas impermeables durante temporada de lluvias"
    ]
  },
  [CargoType.PERISHABLE_FOOD]: {
    riskFactor: 1.30,
    description: "Alimentos que requieren refrigeración y tienen fecha de caducidad corta. Riesgo alto por necesidad de control de temperatura y tiempo de entrega.",
    securityRecommendations: [
      "Transporte en unidades con equipo de refrigeración calibrado",
      "Monitoreo constante de temperatura durante todo el trayecto",
      "Prioridad en rutas para minimizar tiempo de tránsito",
      "Plan de contingencia para fallas en sistema de refrigeración"
    ]
  },
  [CargoType.NON_PERISHABLE_FOOD]: {
    riskFactor: 1.10,
    description: "Alimentos con vida útil prolongada pero que requieren protección contra humedad y plagas.",
    securityRecommendations: [
      "Protección contra humedad y cambios de temperatura",
      "Embalaje resistente a roedores e insectos",
      "Verificación periódica de sellos y empaques"
    ]
  },
  [CargoType.CONSTRUCTION_MATERIALS]: {
    riskFactor: 1.05,
    description: "Materiales resistentes con bajo riesgo de daño durante el transporte.",
    securityRecommendations: [
      "Distribución adecuada del peso en la unidad",
      "Protección contra lluvia para materiales sensibles a humedad",
      "Asegurar correctamente para evitar desplazamientos"
    ]
  },
  [CargoType.AGRICULTURAL_PRODUCTS]: {
    riskFactor: 1.15,
    description: "Productos agrícolas susceptibles a daños por manipulación y condiciones climáticas.",
    securityRecommendations: [
      "Ventilación adecuada para productos frescos",
      "Evitar exposición directa al sol",
      "Separación entre productos para prevenir contaminación cruzada"
    ]
  },
  [CargoType.FURNITURE]: {
    riskFactor: 1.25,
    description: "Mobiliario vulnerable a rayones, abolladuras y roturas durante el transporte.",
    securityRecommendations: [
      "Embalaje protector en esquinas y superficies delicadas",
      "Uso de separadores entre piezas",
      "Asegurar firmemente para evitar movimiento durante el transporte",
      "Manejo cuidadoso en carga y descarga"
    ]
  },
  [CargoType.CLOTHING_TEXTILES]: {
    riskFactor: 1.08,
    description: "Productos textiles con riesgo moderado de daños por humedad o contaminantes.",
    securityRecommendations: [
      "Protección impermeable contra líquidos",
      "Embalaje que evite manchas y olores",
      "Asegurar que el área de carga esté limpia"
    ]
  },
  [CargoType.ELECTRONICS]: {
    riskFactor: 1.40,
    description: "Equipos electrónicos de alto valor y fragilidad, sensibles a golpes y condiciones ambientales.",
    securityRecommendations: [
      "Embalaje con protección antiestática",
      "Material amortiguador para absorber impactos",
      "Control de temperatura y humedad",
      "Seguridad reforzada contra robo",
      "Evitar exposición a campos electromagnéticos fuertes"
    ]
  },
  [CargoType.AUTOMOTIVE_PARTS]: {
    riskFactor: 1.15,
    description: "Componentes automotrices que pueden incluir piezas pesadas y de precisión.",
    securityRecommendations: [
      "Embalaje resistente para piezas pesadas",
      "Protección anticorrosión para metales expuestos",
      "Identificación clara de piezas frágiles o de precisión"
    ]
  },
  [CargoType.HOUSEHOLD_APPLIANCES]: {
    riskFactor: 1.25,
    description: "Electrodomésticos con componentes frágiles y superficies susceptibles a daños.",
    securityRecommendations: [
      "Embalaje original del fabricante cuando sea posible",
      "Refuerzo adicional en esquinas y bordes",
      "Sujeción que evite cualquier movimiento dentro del vehículo",
      "Manipulación por personal capacitado"
    ]
  },
  [CargoType.STEEL_METALS]: {
    riskFactor: 1.05,
    description: "Productos metálicos resistentes pero pesados que requieren consideraciones de distribución de peso.",
    securityRecommendations: [
      "Distribución adecuada del peso en la plataforma",
      "Protección contra humedad para prevenir oxidación",
      "Sujeción reforzada para evitar desplazamientos"
    ]
  },
  [CargoType.CHEMICALS]: {
    riskFactor: 1.50,
    description: "Productos químicos que pueden ser corrosivos, inflamables o reactivos, requiriendo manejo especializado.",
    securityRecommendations: [
      "Cumplimiento estricto de normativas para transporte de químicos",
      "Separación adecuada entre productos incompatibles",
      "Equipo especializado y señalización según normas",
      "Personal con certificación para manejo de químicos",
      "Plan de respuesta a emergencias por derrames"
    ]
  },
  [CargoType.HAZARDOUS_MATERIALS]: {
    riskFactor: 1.80,
    description: "Materiales con clasificación de peligrosidad que requieren permisos especiales y protocolos estrictos.",
    securityRecommendations: [
      "Permisos y documentación especial obligatoria",
      "Unidades certificadas para transporte de materiales peligrosos",
      "Conductores con capacitación y certificación especial",
      "Rutas predeterminadas con evaluación de riesgos",
      "Coordinación con autoridades de emergencia",
      "Sistemas de contención secundaria para derrames"
    ]
  },
  [CargoType.FRAGILE_ITEMS]: {
    riskFactor: 1.45,
    description: "Artículos con alta fragilidad que requieren embalaje especializado y manejo cuidadoso.",
    securityRecommendations: [
      "Embalaje de alta protección con materiales amortiguadores",
      "Señalización clara de fragilidad en todos los empaques",
      "Carga separada de artículos pesados",
      "Verificaciones periódicas durante el trayecto",
      "Conducción suave evitando maniobras bruscas"
    ]
  },
  [CargoType.HIGH_VALUE_GOODS]: {
    riskFactor: 1.60,
    description: "Mercancía de alto valor que representa un riesgo elevado de robo y requiere medidas de seguridad intensivas.",
    securityRecommendations: [
      "Escoltas de seguridad durante todo el trayecto",
      "Vehículos con sistemas GPS y seguimiento en tiempo real",
      "Rutas variables y no predecibles",
      "Comunicación constante con base de operaciones",
      "Seguro especializado de alto valor",
      "Conductores verificados con antecedentes limpios"
    ]
  },
  [CargoType.MACHINERY]: {
    riskFactor: 1.30,
    description: "Equipos y maquinaria pesada con componentes sensibles y distribución de peso compleja.",
    securityRecommendations: [
      "Estudio previo de rutas para verificar capacidad de puentes y vías",
      "Permisos especiales para cargas sobredimensionadas",
      "Anclajes y sujeciones reforzados",
      "Protección especial para componentes hidráulicos y electrónicos",
      "Vehículos escolta para dimensiones especiales"
    ]
  },
  [CargoType.LIVESTOCK]: {
    riskFactor: 1.45,
    description: "Animales vivos que requieren consideraciones de bienestar animal, alimentación y descanso.",
    securityRecommendations: [
      "Vehículos acondicionados para transporte de animales",
      "Ventilación y temperatura controlada",
      "Paradas programadas para alimentación y descanso",
      "Personal capacitado en manejo de ganado",
      "Permisos sanitarios y de movilización",
      "Plan de contingencia para emergencias veterinarias"
    ]
  },
  [CargoType.PHARMACEUTICALS]: {
    riskFactor: 1.40,
    description: "Productos farmacéuticos sensibles a temperatura y que pueden requerir cadena de frío y seguridad adicional.",
    securityRecommendations: [
      "Control y registro continuo de temperatura",
      "Validación de equipos de refrigeración",
      "Embalaje térmico calificado",
      "Seguridad reforzada contra robo",
      "Documentación exhaustiva de la cadena de custodia",
      "Procedimientos para gestión de desviaciones de temperatura"
    ]
  },
  [CargoType.WOOD_PRODUCTS]: {
    riskFactor: 1.05,
    description: "Productos de madera relativamente resistentes pero que requieren protección contra humedad.",
    securityRecommendations: [
      "Protección contra lluvia y humedad excesiva",
      "Sujeción adecuada para evitar desplazamientos",
      "Protección en bordes para maderas finas o tratadas"
    ]
  },
  [CargoType.OTHER]: {
    riskFactor: 1.20,
    description: "Categoría general para mercancías que no encajan en otras clasificaciones, se aplica un factor de riesgo moderado.",
    securityRecommendations: [
      "Evaluación caso por caso del riesgo específico",
      "Embalaje adecuado según características del producto",
      "Documentación detallada del contenido",
      "Consideraciones especiales a determinar según la mercancía"
    ]
  }
};

/**
 * Returns cargo risk details in the appropriate language
 * @param cargoType The cargo type
 * @param language The current language (es/en)
 * @returns CargoRiskData object with details in the right language
 */
export const getCargoRiskDetails = (
  cargoType: CargoType | undefined, 
  language: string = 'es'
): CargoRiskData | undefined => {
  if (!cargoType) return undefined;
  
  return language === 'en' 
    ? CARGO_RISK_DETAILS_EN[cargoType] 
    : CARGO_RISK_DETAILS_ES[cargoType];
};